//@flow
import * as React from 'react';
import IncrementalPjpDataSelectionTable from './IncrementalPjpDataSelectionTable';
import { FilterBuilder } from 'common/models/internal/Filter';
import { IncludeTypeEnums } from 'common/enums/EntityMasterEnum';
import { useSelector } from 'react-redux';
import { clientSelector } from 'common/redux/selectors/queryParamsSelectors';
import FilterListBuilder from 'common/models/helpers/FilterListBuilder';
import PjpActions from 'common/redux/actions/pjpMaster';
import { MasterOutlet } from 'common/models/internal/MasterOutlet';
import { DEFAULT_COLUMNS } from 'common/entity/outletMaster/OutletMasterContainer';
import { useTranslation } from 'react-i18next';
import { IncrementalPjpContext } from './IncrementalPjpContext';

type Props = {
    title: string,
    customActions: {
        onNext: ({}, transitionOnly: boolean) => void,
        onCancel: () => void
    },
    errorMessage: string,
    match: $FixMe,
    location: $FixMe,
    getExtraFiltersForFetchData: () => Array<FilterBuilder>
};

const IncrementalPjpOutletsContainer = (props: Props): React.Node => {
    const clientId = useSelector(state => clientSelector(state, props));

    const outletApiFilters = React.useMemo(() => {
        const statusFilter = new FilterBuilder()
            .withName('status')
            .withOperation('EQUALS')
            .withValue('ACTIVE');

        return new FilterListBuilder()
            .addAll([statusFilter, ...props.getExtraFiltersForFetchData()])
            .build();
    }, [props]);

    const getNewOutlets = React.useCallback(
        ({
            allExistingRecords
        }: {
            allExistingRecords: Array<MasterOutlet>
        }) => {
            const params = {
                clientId,
                include: IncludeTypeEnums.SalesCategory
            };
            const body = {
                page: 1,
                size: 100,
                sortingInfo: [],
                filters: outletApiFilters
            };
            const options = {};
            return PjpActions.fetchAllActiveOutlets(params, body, options).then(
                ({ entities }) => {
                    const existingOutletIds = allExistingRecords.map(
                        outlet => outlet.id
                    );
                    const filteredNewOutlets = entities.entities.filter(
                        outlet => existingOutletIds.indexOf(outlet.id) === -1
                    );
                    return filteredNewOutlets;
                }
            );
        },
        [clientId, outletApiFilters]
    );

    const getExistingEntity = React.useCallback(() => {
        const params = {
            clientId,
            pjpId: props.match?.params.pjpId,
            include: IncludeTypeEnums.SalesCategory
        };
        const body = {};
        const options = {};
        return PjpActions.fetchPjpOutletsCall(params, body, options).then(
            ({ entities }) => entities.outlets
        );
    }, [clientId, props.match.params.pjpId]);

    const { t } = useTranslation('entityManagement');

    const { config, onOutletsNext } = React.useContext(IncrementalPjpContext);

    const _onOutletsNext = React.useCallback(
        selectedRoes => {
            props.customActions.onNext({}, true);
            onOutletsNext(selectedRoes);
        },
        [onOutletsNext, props]
    );

    return (
        <IncrementalPjpDataSelectionTable
            {...props}
            onNext={_onOutletsNext}
            key="outlet"
            getNewEntity={getNewOutlets}
            getExistingEntity={getExistingEntity}
            EntityModel={MasterOutlet}
            filters={outletApiFilters}
            defaultColumns={DEFAULT_COLUMNS}
            entitySettingsName="outletSettings"
            tabTitle={t('entities:outlet')}
            existingTabTitle={t('pjpCreate.incrementalPlan.existingOutlets')}
            newTabTitle={t('pjpCreate.incrementalPlan.newOutlets')}
            allowExistingSelection={config.oldOutletsSelection}
            allowNewSelection={config.newOutletsSelection}
        />
    );
};

export default IncrementalPjpOutletsContainer;
